import { useMemo, useCallback } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { CalculCodeStateKeys, CalculCodeState } from '@batical/types/src'
import { selectActiveContextValueWithCalculCode } from '../Calculs/CalculsSelector'
import { ReduxState } from '../reducers'
import { updateContextValue } from '../Calculs/CalculsAction'

// Helper type to get field type from page code and field name
type GetFieldType<
  TPageCode extends CalculCodeStateKeys,
  TField extends keyof CalculCodeState[TPageCode]
> = CalculCodeState[TPageCode][TField]

/**
 * Legacy hooks - use useContextValue instead for better type safety
 * @deprecated Use useContextValue for type-safe context access
 */
export function useContextNumber(
  variableName: string,
  calcul_code: CalculCodeStateKeys,
  doCalcul: boolean = true
): [number, (val: number) => void] {
  return useContextValue(variableName, calcul_code, doCalcul) as [number, (val: number) => void]
}

/**
 * @deprecated Use useContextValue for type-safe context access
 */
export function useContextString(
  variableName: string,
  calcul_code: CalculCodeStateKeys,
  doCalcul: boolean = true
): [string, (val: string) => void] {
  return useContextValue(variableName, calcul_code, doCalcul) as [string, (val: string) => void]
}

/**
 * @deprecated Use useContextValue for type-safe context access
 */
export function useContextBoolean(
  variableName: string,
  calcul_code: CalculCodeStateKeys,
  doCalcul: boolean = true
): [boolean, (val: boolean) => void] {
  return useContextValue(variableName, calcul_code, doCalcul) as [boolean, (val: boolean) => void]
}

// Type-safe useContextValue with PAGE_CODE and field name inference
export function useContextValue<TPageCode extends CalculCodeStateKeys, TField extends keyof CalculCodeState[TPageCode]>(
  variableName: TField,
  calcul_code: TPageCode,
  doCalcul?: boolean
): [GetFieldType<TPageCode, TField>, (val: GetFieldType<TPageCode, TField>) => void]

// Fallback overload for unknown page codes or string literals
export function useContextValue(variableName: string, calcul_code: CalculCodeStateKeys, doCalcul?: boolean): [any, (val: any) => void]

// Implementation
export function useContextValue(
  variableName: string,
  calcul_code: CalculCodeStateKeys,
  doCalcul: boolean = true
): [any, (val: any) => void] {
  const dispatch = useDispatch()
  const selectContext = useMemo(selectActiveContextValueWithCalculCode, [])
  const value = useSelector((state: ReduxState) => selectContext(state, calcul_code, variableName))

  const update = useCallback(
    (val: any) => {
      dispatch(updateContextValue(variableName, val, calcul_code, doCalcul))
    },
    [dispatch, variableName, calcul_code, doCalcul]
  )
  return [value, update]
}
